// @client/utils/urlUtils.js

import { formatApprovalNumber } from '@client/utils/helpers';

/**
 * Extract identifier from URL path
 * Supports both ID and name-based URLs
 * e.g., /matiere-actives/details/123/ or /matiere-actives/details/substance-name/
 */
export const getActiveSubstanceIdentifierFromUrl = () => {
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);

  // Find the segment after 'details'
  const detailsIndex = segments.findIndex((segment) => segment === 'details');
  if (detailsIndex !== -1 && detailsIndex < segments.length - 1) {
    return segments[detailsIndex + 1];
  }

  return null;
};

/**
 * Check if identifier is UUID format
 */
export const isUUID = (identifier) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(identifier);
};

/**
 * Generate URL for active substance details
 */
export const getActiveSubstanceDetailUrl = (identifier) => {
  return `/matiere-actives/details/${identifier}/`;
};

/**
 * Build the URL to the Canadian pesticide label registry for a given approval number.
 * @param {string} approvalNumber - The product's approval/registration number
 * @returns {string} Full URL to the Health Canada label search
 */
export const buildLabelUrl = (approvalNumber) => {
  const base = 'https://pr-rp.hc-sc.gc.ca/ls-re/result-fra.php';
  // F36/C10: use the client-validated format (p_search_label reaches the exact
  // product label), and strip the trailing ".00" so the number matches the
  // ARLA record (e.g. "33523", not "33523.00").
  const label = formatApprovalNumber(approvalNumber);
  const params = new URLSearchParams({
    p_search_label: label ?? '',
    searchfield1: 'NONE',
    operator1: 'CONTAIN',
    criteria1: '',
    logicfield1: 'AND',
    searchfield2: 'NONE',
    operator2: 'CONTAIN',
    criteria2: '',
    logicfield2: 'AND',
    searchfield3: 'NONE',
    operator3: 'CONTAIN',
    criteria3: '',
    logicfield3: 'AND',
    searchfield4: 'NONE',
    operator4: 'CONTAIN',
    criteria4: '',
    logicfield4: 'AND',
    p_operatordate: '=',
    p_criteriadate: '',
    p_status_reg: 'REGISTERED',
    p_searchexpdate: 'EXP',
  });
  return `${base}?${params.toString()}`;
};

/**
 * Update URL without page reload (for SEO and navigation)
 */
export const updateUrlWithActiveSubstance = (identifier, name = null) => {
  const newUrl = name
    ? `/matieres-actives/details/${encodeURIComponent(name)}/`
    : `/matieres-actives/details/${identifier}/`;

  if (window.location.pathname !== newUrl) {
    window.history.replaceState({}, '', newUrl);
  }
};
